// Stage Mail-V2 Composer (2026-07-23): plainTextHtml — safe plain↔html 转换工具。
//
// Purpose:
//   Mail V2 老 draft 兼容:当数据库存的是 body_text 但 body_html 为空时,
//   编辑器初值需要把纯文本安全转换为 HTML 喂给 TipTap。
//   反之亦然:编辑完成时若外部需要纯文本,也要从 HTML 安全提取。
//
// 安全规则(plainTextToHtml):
//   - 所有 HTML 元字符(<,>,&,",') 都 escape
//   - 不会把用户输入解释为 HTML / script / on* 事件
//   - 空行分隔段落,单换行转 <br />
//   - 不插链接 / 图片 / 列表 — 纯文本就该保持纯文本
//
// 安全规则(htmlToPlainText):
//   - 把 HTML 块级元素用换行分隔(段落、列表、标题、换行 <br />)
//   - 剥掉所有 HTML 标签(<...>)
//   - 解码最常见 HTML 实体(&amp; &lt; &gt; &quot; &#39; &nbsp;)
//   - 不解析 / 不执行任何 HTML;不查 DOM(纯字符串处理)
//
// 边界:
//   - 不依赖 DOM(SSR 安全)
//   - 不依赖 DOMPurify(后端 sanitize 已保证;前端编辑前不再 sanitize,
//     避免再次漂白用户当前未持久化内容)
//   - 字符串实现简单可读;不追求最强 Unicode Normalization。

/**
 * Escape HTML 元字符。覆盖 &, <, >, ", '。
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * 反向 escape。仅在 htmlToPlainText 中使用 — 我们 strip 完标签后
 * 需要把 &amp; 还原成 & 等。注意:顺序固定 amp → lt → gt → quot → #39。
 */
function unescapeHtml(s: string): string {
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");
}

/**
 * 把纯文本安全转换为 HTML。
 *
 * 规则:
 *   - 空字符串 → ""
 *   - null/undefined → ""
 *   - 段落分隔:连续两个及以上换行(\n\n / \n\n\n)把文本切成多段
 *   - 每段内:单换行 → <br />,其余 escape
 *   - 段首尾空白 trim
 *   - 全空段落剔除
 *   - 最终:每段包 <p>...</p>(空段落也用 <p></p> 而不是直接 "")
 *
 * 故意不做的:
 *   - 不识别 URL 自动加链接(避免引入 http:// 开头的转义误判 + 不引入 sanitize 风险)
 *   - 不识别 "## " 这种 markdown(避免引入 markdown 解析器)
 *   - 不保留原始缩进(tipTap <p> 自动归一)
 *
 * 例子:
 *   plainTextToHtml("hello\nworld\n\nfoo") ===
 *     "<p>hello<br />world</p><p>foo</p>"
 */
export function plainTextToHtml(plain: string | null | undefined): string {
  if (plain == null) return "";
  const s = String(plain);
  if (s === "") return "";
  // 用 \n\n 切段(允许 \r\n / \r 规范化)
  const normalized = s.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const blocks = normalized.split(/\n{2,}/);
  const out: string[] = [];
  for (const block of blocks) {
    const trimmed = block.replace(/^\s+|\s+$/g, "");
    if (trimmed === "") continue;
    // 单换行 → <br />
    const lines = trimmed.split("\n");
    const html = lines.map(escapeHtml).join("<br />");
    out.push(`<p>${html}</p>`);
  }
  if (out.length === 0) return "";
  return out.join("");
}

/**
 * 把 HTML 安全转换为纯文本。
 *
 * 规则:
 *   - 块级结束标签(p / div / li / h1-h6 / blockquote / tr / pre)在末尾加 \n
 *   - <br /> / <br> 转 \n
 *   - 其他标签直接 strip
 *   - HTML 实体反转义
 *   - 连续 \n 折叠为单个
 *   - 头尾 trim
 *
 * 故意不做:
 *   - 不解析 DOM(纯字符串处理)
 *   - 不识别 entity 数字引用(如 &#x4e2d;)— TipTap getText 已经给我们 ProseMirror
 *     的纯文本节点,这函数主要用于测试 / fallback。
 *
 * 用途:
 *   真实编辑器编辑完后的纯文本由 editor.getText() 直接给出(更准确);
 *   本函数用于:外部导入 HTML 时快速取纯文本 / 测试。
 */
export function htmlToPlainText(html: string | null | undefined): string {
  if (html == null) return "";
  let s = String(html);
  if (s === "") return "";
  // 块级结束标签 → 换行(p / div / li / h1-6 / blockquote / tr / pre / section / article)
  s = s.replace(
    /<\/?(?:p|div|li|h[1-6]|blockquote|tr|pre|section|article)[^>]*>/gi,
    "\n",
  );
  // <br> 各种写法
  s = s.replace(/<br\s*\/?>/gi, "\n");
  // 剥其余标签
  s = s.replace(/<[^>]+>/g, "");
  // 反转义
  s = unescapeHtml(s);
  // 折叠连续 \n + trim
  s = s.replace(/\n{2,}/g, "\n").replace(/^\s+|\s+$/g, "");
  return s;
}
